// Client-side extraction + upload helpers for Stew chat attachments.
// The stew-attachments bucket is private; uploads land under `<auth.uid()>/...`
// and RLS enforces owner-only reads.

import { supabase } from "@/integrations/supabase/client";

export const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_FILES_PER_MESSAGE = 3;
export const PER_FILE_CHAR_BUDGET = 15000;

export const ACCEPTED_MIME = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
];

export const ACCEPT_ATTR =
  ".pdf,.txt,.md,.markdown,.docx,.png,.jpg,.jpeg,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg";

export type AttachmentKind = "pdf" | "text" | "docx" | "image";

export interface PreparedAttachment {
  id: string; // local uid
  file: File;
  kind: AttachmentKind;
  extractedText: string | null;
  truncated: boolean;
  storagePath: string | null;
}

export function detectKind(file: File): AttachmentKind | null {
  const name = file.name.toLowerCase();
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (
    file.type === "text/plain" ||
    file.type === "text/markdown" ||
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    name.endsWith(".markdown")
  )
    return "text";
  if (
    file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    name.endsWith(".docx")
  )
    return "docx";
  if (file.type.startsWith("image/") || /\.(png|jpe?g)$/i.test(name))
    return "image";
  return null;
}

async function extractText(file: File, kind: AttachmentKind): Promise<string> {
  if (kind === "text") return await file.text();
  if (kind === "docx") {
    const mammoth = await import("mammoth/mammoth.browser");
    const buf = await file.arrayBuffer();
    const res = await (mammoth as any).extractRawText({ arrayBuffer: buf });
    return String(res?.value ?? "");
  }
  if (kind === "pdf") {
    const pdfjs: any = await import("pdfjs-dist/build/pdf.mjs");
    const workerUrl = (
      await import("pdfjs-dist/build/pdf.worker.mjs?url" as any)
    ).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf, isEvalSupported: false })
      .promise;
    const pages: string[] = [];
    const max = Math.min(doc.numPages, 50);
    for (let i = 1; i <= max; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      pages.push(
        content.items.map((it: any) => ("str" in it ? it.str : "")).join(" "),
      );
    }
    return pages.join("\n\n");
  }
  return "";
}

export async function prepareAttachment(
  file: File,
): Promise<PreparedAttachment> {
  const kind = detectKind(file);
  if (!kind) throw new Error(`Unsupported file type: ${file.name}`);
  let extractedText: string | null = null;
  let truncated = false;
  if (kind !== "image") {
    const raw = await extractText(file, kind);
    if (raw.length > PER_FILE_CHAR_BUDGET) {
      extractedText = raw.slice(0, PER_FILE_CHAR_BUDGET);
      truncated = true;
    } else {
      extractedText = raw;
    }
  }
  return {
    id: crypto.randomUUID(),
    file,
    kind,
    extractedText,
    truncated,
    storagePath: null,
  };
}

export async function uploadAttachment(
  userId: string,
  att: PreparedAttachment,
): Promise<string> {
  const safeName = att.file.name.replace(/[^\w.\-]+/g, "_");
  const path = `${userId}/${Date.now()}-${att.id}-${safeName}`;
  const { error } = await supabase.storage
    .from("stew-attachments")
    .upload(path, att.file, {
      contentType: att.file.type || "application/octet-stream",
      upsert: false,
    });
  if (error) throw error;
  return path;
}

export function buildAttachmentContext(
  atts: PreparedAttachment[],
): { contextText: string; imageNotice: boolean } {
  const parts: string[] = [];
  let imageNotice = false;
  for (const a of atts) {
    if (a.kind === "image") {
      imageNotice = true;
      continue;
    }
    if (!a.extractedText) continue;
    const trunc = a.truncated
      ? ` (truncated to ${PER_FILE_CHAR_BUDGET} characters)`
      : "";
    parts.push(
      `The member attached [${a.file.name}]${trunc}. Its contents:\n${a.extractedText}`,
    );
  }
  return { contextText: parts.join("\n\n---\n\n"), imageNotice };
}
